import {Action, IAction, IActionMenu} from "lib/commons/actions";
import {JSX} from "lib/commons/xml/dom";
import {IReg, REG} from "lib/commons/registry";
import {IDepotCtx, InfoFocusRes, JCidMetas, resPath} from "lib/store/res";
import {JNodeShortProps, URLTREE} from "lib/store/urlTree";
import {EStoreAspects} from "lib/store/depot";
import {POPUP} from "back/commons/widgets/popups";
import {AddByImport, AddFolder, Depot2ResCtxAction, ResEditAction, VisStateInvisible, VisStateLimited, VisStateVisible} from "back/store/actions/resActions";
import {JNodeTreeB} from "back/store/views/utBrowser";

/**
 * Action de base sur un ou plusieurs Node d'un urlTree.
 */
export abstract class ResAction<C extends IDepotCtx> extends Action<C> {

	/** Tous les élements de la liste doivent matcher ce prédicat pour être visible. */
	allMustMatch?: ((node: JNodeShortProps) => boolean);

	/** La liste doit contenir et un seul élément (et doit matcher allMustMatch si renseigné) pour être visible. */
	exactOne?: boolean;

	/** La liste doit contenir 0 ou un seul élément (et doit matcher allMustMatch si renseigné) pour être visible. */
	zeroOrOne?: boolean;

	/** Pour être visible, au moins un élément doit être dans la liste (si true) ou matcher le prédicat (d'autres éléments sélectionnés peuvent ne pas le matcher). */
	atLeastOne?: boolean | ((node: JNodeShortProps) => boolean);

	/* Perms appliquées sur chaque node (avec ses roles) pour évaluer la visibilité de l'action. */
	protected _visNodePerms: string[];

	/* Perms appliquées sur chaque src (avec ses roles) pour évaluer l'activation de l'action. */
	protected _enableNodePerms: string[];

	/**
	 * Déclarations des actions par défaut
	 */
	static add = function (action: IAction<IDepotCtx>, accelKey?: string, reg: IReg<any> = REG.reg) {
		reg.addToList("actions:store:resList", action.getId(), 1, action);
		if (accelKey) reg.addToList("accelkeys:store:resList", action.getId(), 1, action);
	}


	/**
	 * Elimine les res qui sont des descendants d'autres res dans cette liste
	 * pour ne garder que les res de plus haut niveau.
	 */
	static restrictToRoots(resList: JNodeShortProps[]) {
		resList.sort(URLTREE.defaultResSortFn); //Tri garantissant que le path d'un dossier précède ses descendants.
		const newList: JNodeShortProps[] = [resList[0]];
		let lastPath = newList[0].permaPath;
		for (let i = 1; i < resList.length; i++) {
			const n = resList[i];
			if (URLTREE.isDescendantPath(lastPath, n.permaPath)) continue;
			newList.push(n);
			lastPath = n.permaPath;
		}
		return newList;
	}

	isVisible(ctx: C): boolean {
		let resList = ctx.resList;
		if (this.atLeastOne != null) {
			if (typeof this.atLeastOne === "function") {
				resList = ctx.resList.filter(this.atLeastOne);
				if (resList.length < 1) return false;
			} else if (this.atLeastOne && ctx.resList.length < 1) return false;
		}
		if (this.exactOne && ctx.resList.length !== 1) return false;
		if (this.zeroOrOne && ctx.resList.length > 1) return false;
		if (!super.isVisible(ctx)) return false;
		if (this.allMustMatch && !ctx.resList.every(this.allMustMatch)) return false;
		return this.checkNodePermsForAll(this._visNodePerms, resList, ctx);
	}

	isEnabled(ctx: C): boolean {
		if (!super.isEnabled(ctx)) return false;
		return this.checkNodePermsForAll(this._enableNodePerms, typeof this.atLeastOne === "function" ? ctx.resList.filter(this.atLeastOne) : ctx.resList, ctx)
	}

	getFirstPermaPath(ctx: C): resPath {return ctx.resList.length >= 1 ? ctx.resList[0].permaPath : null}

	getFirstNode(ctx: C): JNodeShortProps {return ctx.resList.length >= 1 ? ctx.resList[0] : null}

	checkNodePermsForAll(perms: string | string[], resList: JNodeShortProps[], ctx: C): boolean {
		if (perms) {
			const reg = REG.getReg(ctx);
			if (ctx.resList.length === 0) {
				//Aucun src listé => perm sur l'atelier
				if (!reg.hasPerm(perms)) return false;
			} else {
				for (const props of resList) if (!reg.hasPermission(perms, props.roles)) return false;
			}
		}
		return true
	}

	requireNodeVisiblePerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireNodeVisiblePerm(perms[i]);
			} else {
				if (!this._visNodePerms) this._visNodePerms = [];
				if (this._visNodePerms.indexOf(perms) == -1) this._visNodePerms.push(perms);
			}
		}
		return this;
	}

	requireNodeEnabledPerm(perms: string | string[]): this {
		if (perms) {
			if (Array.isArray(perms)) {
				for (let i = 0; i < perms.length; i++) this.requireNodeEnabledPerm(perms[i]);
			} else {
				if (!this._enableNodePerms) this._enableNodePerms = [];
				if (this._enableNodePerms.indexOf(perms) == -1) this._enableNodePerms.push(perms);
			}
		}
		return this;
	}
}


/** Action qui affiche et focus une et une seule res. */
export class FocusRes<C extends IDepotCtx> extends ResAction<C> {

	constructor(id?: string) {
		super(id || 'focusRes');
		this._label = "Afficher";
		this._icon = "/@skin@/store/actions/showRes.svg";
		this._group = "open";
		this.exactOne = true;
	}

	isVisible(ctx: C): boolean {
		if (!ctx.infoBroker) return false;
		return super.isVisible(ctx);
	}

	execute(ctx: C, ev?: Event) {
		ctx.infoBroker.dispatchInfo(new InfoFocusRes(this.getFirstPermaPath(ctx)), ctx.emitter);
	}
}

//
// export class FocusUnversionnedLeafRes<C extends IDepotCtx> extends FocusRes<C> {
// 	execute(ctx: C, ev?: Event) {
// 		ctx.infoBroker.dispatchInfo(new InfoFocusRes(URLTREE.extractUnversionedLeafPath(this.getFirstPermaPath(ctx))), ctx.emitter);
// 	}
// }
//
// export class FocusUnversionnedRes<C extends IDepotCtx> extends FocusRes<C> {
// 	execute(ctx: C, ev?: Event) {
// 		ctx.infoBroker.dispatchInfo(new InfoFocusRes(URLTREE.extractUnversionedPath(this.getFirstPermaPath(ctx))), ctx.emitter);
// 	}
// }

export class FocusLiveRes<C extends IDepotCtx> extends FocusRes<C> {
	execute(ctx: C, ev?: Event) {
		const target = this.getFirstNode(ctx);
		ctx.infoBroker.dispatchInfo(new InfoFocusRes(ctx.reg.env.resTypes.getResTypeFor(target).livePath(target.permaPath)), ctx.emitter);
	}
}

// export class ShortDescCopy<C extends IDepotCtx> extends ResAction<C> {
// 	constructor(id?: string) {
// 		super(id || 'copy');
// 		this._label = "Copier";
// 		this._group = "clipboard";
// 		this._icon = "/@skin@/wsp/actions/copyItem.svg";
// 		this.atLeastOne = true;
// 	}
//
// 	execute(ctx: IDepotCtx, ev?: Event): Promise<void> {
//
// 	}
//
// 	static SINGLETON = new ShortDescCopy();
// }


export class VisStateResList<C extends IDepotCtx> extends ResAction<C> implements IActionMenu<C> {
	constructor(id?: string) {
		super(id || 'visStateRes');
		this._group = "edit";
		this.atLeastOne = (ni: JNodeShortProps) => !URLTREE.isRootPath(ni.permaPath) && ni.t !== "moved"; //XXX à voir si root versionné ?;
	}

	getLabel(ctx: C): string {
		return ctx.reg.env.resTypes.visStateMenuLabel;
	}

	isVisible(ctx: C): boolean {
		if (!super.isVisible(ctx)) return false;
		return ctx.reg.env.universe.hasAspect(EStoreAspects.trash) || ctx.reg.env.universe.hasAspect(EStoreAspects.unlisted);
	}

	isMenu(ctx: C): this is IActionMenu<C> {return true}

	getDatas(api: "menu", ctx: C): IAction<C>[] | any {
		if (api !== "menu") return;
		const u = ctx.reg.env.universe;
		if (ctx.resList.length === 1) {
			//Single sel
			const actions: IAction<C>[] = [new Depot2ResCtxAction(new VisStateVisible())];
			if (u.hasAspect(EStoreAspects.unlisted)) actions.push(new Depot2ResCtxAction(new VisStateLimited()))
			if (u.hasAspect(EStoreAspects.trash)) actions.push(new Depot2ResCtxAction(new VisStateInvisible()))
			return actions;
		} else {
			//Gestion multiple
			const actions: IAction<C>[] = [new VisStateInListVisible()];
			if (u.hasAspect(EStoreAspects.unlisted)) actions.push(new VisStateInListLimited())
			if (u.hasAspect(EStoreAspects.trash)) actions.push(new VisStateListInvisible())
			return actions;
		}
	}
}


abstract class VisStateInList<C extends IDepotCtx> extends ResAction<C> implements JNodeShortProps {

	trashed: boolean;
	unlisted: boolean;

	constructor(id?: string) {
		super(id);
		this._group = "edit";
		this.initState();
	}

	abstract initState(): void;

	getLabel(ctx: C): string {
		return ctx.reg.env.resTypes.visStateLabel(this);
	}

	getDescription(ctx: C): string {
		return ctx.reg.env.resTypes.visStateDescription(this);
	}

	getIcon(ctx: C): string {
		return ctx.reg.env.resTypes.visStateIconUrl(this);
	}

	abstract stateMatch(ni: JNodeShortProps): boolean

	abstract setState(ni: JNodeShortProps, metas: JCidMetas): JCidMetas

	async execute(ctx: C, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		let resList = ResAction.restrictToRoots(ctx.resList.filter(
			(ni) => !URLTREE.isRootPath(ni.permaPath) && ni.t !== "moved" && !this.stateMatch(ni))
		);
		const count = resList.length;
		if (count === 0) return;
		const progress = POPUP.showProgress(uiCtx, "Changement en cours...");
		try {
			progress.onProgress(0, count);
			for (let i = 0; i < count; i++) {
				const n = resList[i];
				await ResEditAction.sendCidUpdate(this.setState(n, {
					path: n.permaPath,
					olderResId: ""
				}), ctx, uiCtx);
				progress.onProgress(i + 1, count);
			}
		} finally {
			progress.close();
		}
	}
}

class VisStateInListVisible<C extends IDepotCtx> extends VisStateInList<C> {
	initState() {
		this.trashed = false;
		this.unlisted = false;
	}

	stateMatch(ni: JNodeShortProps): boolean {return ni.unlisted !== true && ni.trashed !== true}

	setState(ni: JNodeShortProps, metas: JCidMetas) {
		if (ni.trashed) metas.trashed = false;
		if (ni.unlisted) metas.unlisted = false;
		return metas;
	}
}

class VisStateInListLimited<C extends IDepotCtx> extends VisStateInList<C> {
	initState() {
		this.trashed = false;
		this.unlisted = true;
	}

	stateMatch(ni: JNodeShortProps): boolean {return ni.unlisted === true && ni.trashed !== true}

	setState(ni: JNodeShortProps, metas: JCidMetas) {
		if (ni.trashed) metas.trashed = false;
		metas.unlisted = true;
		return metas;
	}
}

class VisStateListInvisible<C extends IDepotCtx> extends VisStateInList<C> {
	initState() {
		this.trashed = true;
		this.unlisted = false;
	}

	stateMatch(ni: JNodeShortProps): boolean {return ni.trashed === true}

	setState(ni: JNodeShortProps, metas: JCidMetas) {
		metas.trashed = true;
		if (ni.unlisted) metas.unlisted = false;
		return metas;
	}
}


export class RemoveResList<C extends IDepotCtx> extends ResAction<C> {

	constructor(id?: string) {
		super(id || 'removeResList');
		this._label = "Supprimer définitivement";
		this._description = "Supprimer définitivement ces éléments (restauration impossible)";
		this._icon = "/@skin@/store/actions/removeRes.svg";
		this._group = "edit";
		this._enablePerms = "action.store#remove.res";
		this.atLeastOne = (node: JNodeShortProps) => !URLTREE.isRootPath(node.permaPath); //XXX à voir si root versionné ?;
	}

	async execute(ctx: C, ev?: Event) {
		const uiCtx = ResEditAction.getUiCtx(ctx, ev);
		const resList = ResAction.restrictToRoots(ctx.resList) as JNodeTreeB[];
		const count = resList.length;
		if (await POPUP.confirm(`Supprimer définitivement ces ${count} éléments ? Attention, aucune restauration ne sera possible.`, uiCtx, {okLbl: "Supprimer définitivement"})) {
			const progress = POPUP.showProgress(uiCtx, "Traitement en cours...");
			try {
				progress.onProgress(0, count);
				for (let i = 0; i < count; i++) {
					const n = resList[i];
					await ResEditAction.sendCidUpdate({
						path: n.permaPath,
						action: n.vcb === 'v' ? "removeNode" : "removeAll"
					}, ctx, uiCtx);
					progress.onProgress(i + 1, count);
				}
			} finally {
				progress.close();
			}
		}
	}
}

export class AddFolderInList<C extends IDepotCtx> extends Depot2ResCtxAction<C> {
	constructor() {
		super(new AddFolder());
	}

	getGroup(ctx: C): string {return "add"}

	getLabel(ctx: C): string {return "Ajouter un dossier"}
}

export class AddByImportInList<C extends IDepotCtx> extends Depot2ResCtxAction<C> {
	constructor() {
		super(new AddByImport());
	}

	getGroup(ctx: C): string {return "add"}

	getLabel(ctx: C): string {return "Ajouter une ressource"}
}


ResAction.add(new AddByImportInList());
ResAction.add(new AddFolderInList());
ResAction.add(new VisStateResList());
ResAction.add(new RemoveResList());